import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  DynamicDatePickerModel,
  DynamicFormControlComponent,
  DynamicFormControlCustomEvent,
  DynamicFormControlLayout,
  DynamicFormLayout,
  DynamicFormLayoutService,
  DynamicFormValidationService,
} from '@ng-dynamic-forms/core';
import { Store } from '@ngrx/store';
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core';
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root';
import { AuthStorageService, UserIdService } from '@spartacus/core';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { CartExtend } from 'src/app/model/cart.model';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';

@Component({
  selector: 'dynamic-material-datepicker',
  templateUrl: './dynamic-material-datepicker.component.html',
  standalone: false,
})
export class DynamicMaterialDatePickerComponent extends DynamicFormControlComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private cartSub = new Subscription();
  @Input() formLayout: DynamicFormLayout;
  @Input() group: FormGroup;
  @Input() layout: DynamicFormControlLayout;
  @Input() model: DynamicDatePickerModel;

  @Output() blur: EventEmitter<any> = new EventEmitter();
  @Output() change: EventEmitter<any> = new EventEmitter();
  @Output() customEvent: EventEmitter<DynamicFormControlCustomEvent> = new EventEmitter();
  @Output() focus: EventEmitter<any> = new EventEmitter();

  @ViewChild('matDatepicker', { static: true }) matDatePicker: MatDatepicker<any>;
  @ViewChild(MatInput, { static: true }) matInput: MatInput;
  minDate: string;
  maxDate: string;
  maxDateOverride: string;
  dateMinMax$: Observable<any>;
  dateFormat: string;
  dateMinMax: any;
  minDeliveryDate: string;
  minLeadTimeOutOfStock: string;
  cart$: Observable<CartExtend>;
  displayInput: string;
  backorderItems: any;
  inStockItems: any;
  userCurrent$: Observable<string> = this.authService.getUserId();
  tomorrow = new Date(new Date().getTime() + 86400000);
  today = new Date(new Date().getTime());
  activeCountry$: Observable<string> = this.countryService.getActive();
  isSameDayDeliveryEnabled: boolean = true;
  sameDayDeliveryEnabledSubscription$: Subscription;
  sameDayDeliveryEnabled$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.SAME_DAY_DELIVERY)
    )
  );
  constructor(
    protected layoutService: DynamicFormLayoutService,
    protected validationService: DynamicFormValidationService,
    public datepipe: DatePipe,
    public checkoutService: CheckoutServiceExtends,
    public activeCartService: ActiveCartFacade,
    protected authService: UserIdService,
    protected authStorageService: AuthStorageService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private countryService: ShimanoCountryService,
    private store: Store<StateWithMultiCart>,
    private componentService: ComponentService,
    @Inject(MAT_FORM_FIELD_DEFAULT_OPTIONS) @Optional() public LABEL_OPTIONS: any
  ) {
    super(layoutService, validationService);
    this.cart$ = this.activeCartService.getActive();
    this.cartSub = this.cart$.subscribe((data: CartExtend) => {
      this.minDeliveryDate = data.minDeliveryDate || null;
      if (typeof data.entries !== 'undefined') {
        this.backorderItems = data.entries.filter((item) => item.backordered);
        this.inStockItems = data.entries.filter((item) => !item.backordered);
      }
      this.dateMinMax$ = this.checkoutService.queryDatesMinMax();
      this.dateMinMax$.pipe(take(1)).subscribe((res) => {
        this.dateFormat = res.dateFormat;
        this.dateMinMax = res;
        const orderType = this.group.get('orderType').value;

        if (orderType === 'PO') {
          const minPlannedOrderDate = this.dateMinMax.minPlannedOrderDate || null;
          this.minDeliveryDate = this.minDeliveryDate !== null ? this.minDeliveryDate : minPlannedOrderDate;
          this.componentService.plannedOrderMinDeliveryDate$.next(this.minDeliveryDate);
        }
        if (this.minDeliveryDate) {
          if (orderType === 'PO') {
            // minPlannedOrderDate: from checkoutConfig API
            // minDeliveryDate: from carts API
            if (new Date(this.dateMinMax.minPlannedOrderDate) >= new Date(this.minDeliveryDate)) {
              const minDatePlanned: any = this.datepipe.transform(this.dateMinMax.minPlannedOrderDate, 'yyyy-MM-dd');
              const minDatePlannedRaw: any = moment(minDatePlanned, 'YYYY-MM-DD').add(1, 'day');
              this.minDate = this.datepipe.transform(minDatePlannedRaw, 'yyyy-MM-dd');
              const dateRawPlanned: any = moment(minDatePlannedRaw, 'YYYY-MM-DD').add(1, 'year');
              this.maxDate = this.datepipe.transform(dateRawPlanned, 'yyyy-MM-dd');
            } else {
              this.minDate = this.datepipe.transform(this.minDeliveryDate, 'yyyy-MM-dd');
              const dateRaw: any = moment(this.minDate, 'YYYY-MM-DD').add(1, 'year');
              this.maxDate = this.datepipe.transform(dateRaw, 'yyyy-MM-dd');
            }
          } else {
            this.minDate = this.updateStandardOrderMinDate(orderType, this.minDeliveryDate);
            const dateRaw: any = moment(this.minDate, 'YYYY-MM-DD').add(1, 'year');
            this.maxDate = this.datepipe.transform(dateRaw, 'yyyy-MM-dd');
          }
        } else {
          this.minDate = this.updateStandardOrderMinDate(orderType);
          const dateRaw: any = moment(this.minDate, 'YYYY-MM-DD').add(1, 'year');
          this.maxDate = this.datepipe.transform(dateRaw, 'yyyy-MM-dd');
        }

        this.updatedate(this.minDate);
      });
    });
  }
  get userCurrent(): string {
    let userCurrent: string;
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }

  filterDate = (d: Date): boolean => {
    if (this.minLeadTimeOutOfStock) {
      const ranges = this.getDates(this.minDate, this.maxDate);
      const overtimeItems = this.backorderItems.filter((item: any) => item.inventoryLeadTime >= 52);
      const maxDate = moment(this.maxDate);
      let inventoryLeadTime = 0;
      overtimeItems.forEach((item: any) => {
        const overtimeItem = moment().add(item.inventoryLeadTime, 'week');
        if (item.inventoryLeadTime > inventoryLeadTime && overtimeItem > maxDate) {
          inventoryLeadTime = item.inventoryLeadTime;
          this.maxDateOverride = overtimeItem.format('YYYY-MM-DD');
        }
        if (ranges.indexOf(item) === -1) {
          ranges.push(moment(overtimeItem).format('YYYY-MM-DD'));
        }
      });
      const datepicker = moment(d).format('YYYY-MM-DD');
      return ranges.find((item: any) => item === datepicker);
    } else {
      return true;
    }
  };
  getDates(startDate: any, stopDate: any) {
    const dateArray: any = [];
    let currentDateMoment = moment(startDate);
    const stopDateMoment: any = moment(stopDate);
    while (currentDateMoment <= stopDateMoment) {
      dateArray.push(moment(currentDateMoment).format('YYYY-MM-DD'));
      currentDateMoment = moment(currentDateMoment).add(1, 'days');
    }
    return dateArray;
  }
  ngOnInit() {
    this.sameDayDeliveryEnabledSubscription$ = this.sameDayDeliveryEnabled$.subscribe((data) => {
      this.isSameDayDeliveryEnabled = data;
    });
    if (this.group.get('requestedDeliveryDate')) {
      this.subscription.add(
        this.group.get('orderType').valueChanges.subscribe((selectedValue) => {
          this.updateCartTotals(selectedValue);
          if (selectedValue === 'SO') {
            if (this.minLeadTimeOutOfStock) {
              if (Number(this.minLeadTimeOutOfStock) <= 52) {
                const today: any = this.datepipe.transform(new Date(), 'yyyy-MM-dd');
                const newMinDate: any = moment(today, 'YYYY-MM-DD').add(this.minLeadTimeOutOfStock, 'week');
                this.minDate = this.datepipe.transform(newMinDate, 'yyyy-MM-dd');
                const dateRaw: any = moment(newMinDate, 'YYYY-MM-DD').add(1, 'year');
                this.maxDate = this.datepipe.transform(dateRaw, 'yyyy-MM-dd');
              } else {
                const today = this.datepipe.transform(new Date(), 'yyyy-MM-dd');
                const newMinDate: any = moment(today, 'YYYY-MM-DD').add(this.minLeadTimeOutOfStock, 'week');
                this.minDate = this.datepipe.transform(newMinDate, 'yyyy-MM-dd');
                this.maxDate = this.datepipe.transform(newMinDate, 'yyyy-MM-dd');
              }
            } else {
              const standardOrderMinDate = this.componentService.standardOrderMinDate$.getValue();
              const plannedOrderMinDeliverDate = this.componentService.plannedOrderMinDeliveryDate$.getValue();
              const compareMinDeliveryDates = this.minDeliveryDate === plannedOrderMinDeliverDate;
              this.minDate = this.minDeliveryDate
                ? standardOrderMinDate ||
                  this.updateStandardOrderMinDate(selectedValue, compareMinDeliveryDates ? null : this.minDeliveryDate)
                : this.updateStandardOrderMinDate(selectedValue);

              const dateRaw: any = moment(this.minDate, 'YYYY-MM-DD').add(1, 'year');
              this.maxDate = this.datepipe.transform(dateRaw, 'yyyy-MM-dd');
            }
            this.updatedate(this.minDate);
          } else {
            const minDatePlanned: any = this.datepipe.transform(this.dateMinMax.minPlannedOrderDate, 'yyyy-MM-dd');
            const minDatePlannedRaw: any = moment(minDatePlanned, 'YYYY-MM-DD').add(1, 'day');
            this.minDate = this.datepipe.transform(minDatePlannedRaw, 'yyyy-MM-dd');
            const dateRaw: any = moment(this.minDate, 'YYYY-MM-DD').add(1, 'year');
            this.maxDate = this.datepipe.transform(dateRaw, 'yyyy-MM-dd');
            this.updatedate(this.minDate);
          }
        })
      );
    }
    if (this.model.id === 'requestedCancelOrderDate') {
      this.updateRequestedCancelOrderDate();
    }
  }
  onChange(field: any) {
    if (this.group.get('requestedDeliveryDate')) {
      const date = this.datepipe.transform(field.value, 'yyyy-MM-dd');
      this.group.get(this.model.id).setValue(date);
      const formatDate = this.datepipe.transform(field.value, this.dateFormat);
      this.displayInput = formatDate;
      this.change.emit(this.group);
    }
  }

  updatedate(date: any) {
    this.group.get('requestedDeliveryDate').patchValue(date, { emitEvent: false });
    const formatDate = this.datepipe.transform(date, this.dateFormat);
    this.displayInput = formatDate;
    if (this.model.id === 'requestedCancelOrderDate') {
      this.displayInput = null;
      this.group.get('requestedCancelOrderDate').setValue(null);
      this.setRequestedCancelOrderDate();
    }
    this.change.emit(this.group);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.cartSub.unsubscribe();
    this.sameDayDeliveryEnabledSubscription$?.unsubscribe();
  }

  updateCartTotals(type: any) {
    this.checkoutService
      .queryOrderType(type)
      .pipe(take(1))
      .subscribe((cart: Cart) => {
        this.store.dispatch(
          new CartActions.LoadCart({
            userId: this.userCurrent,
            cartId: cart.code,
          })
        );
      });
  }

  setRequestedCancelOrderDate() {
    const newMinDateRaw: any = moment(this.group.get('requestedDeliveryDate').value, 'YYYY-MM-DD').add(14, 'days');
    const newMinDate = this.datepipe.transform(newMinDateRaw, 'yyyy-MM-dd');
    this.minDate = newMinDate;
  }

  updateStandardOrderMinDate(orderType: string, minDeliveryDate: any = null): string {
    const baseDate = minDeliveryDate
      ? minDeliveryDate
      : orderType === 'SO' && this.isSameDayDeliveryEnabled
      ? this.today
      : this.tomorrow;

    const transformedDate = this.datepipe.transform(baseDate, 'yyyy-MM-dd');
    if (orderType === 'SO') {
      this.componentService.standardOrderMinDate$.next(transformedDate);
    }
    return transformedDate;
  }
  updateRequestedCancelOrderDate() {
    this.group.get('requestedDeliveryDate').valueChanges.subscribe((date) => {
      this.displayInput = null;
      this.group.get('requestedCancelOrderDate').setValue(null);
      this.setRequestedCancelOrderDate();
    });
  }
}
